import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';

import {Toast} from '#component/base';
import {useToastQueue} from '#hook';

import {toasterClassName} from './Toaster.css';

const bodyEl = typeof document === 'undefined' ? undefined : document.body;

/**
 * The `Toaster` component is a minimal partial component that renders the
 * current toasts that exist in the state values managed by the `ToastProvider`
 * component.
 *
 * It places them in a portalled wrapper that renders them in the appropriate
 * location within the viewport.
 */
export const Toaster = ({className, ...otherProps}: React.ComponentPropsWithoutRef<'div'>) => {
  const {list, maxVisible, removeToast} = useToastQueue();
  const [windowType, setWindowType] = useState('undefined');
  const isClient = windowType !== 'undefined';

  useEffect(() => {
    if (typeof window !== windowType) {
      setWindowType(typeof window);
    }
  }, [windowType]);

  return isClient && bodyEl
    ? createPortal(
        <div className={`${toasterClassName} ${className}`} {...otherProps}>
          {list.slice(0, maxVisible).map(({data, id, willRemove}, i) => (
            <Toast
              {...data}
              key={id}
              onOpenChange={(nextOpen: boolean) => {
                if (!nextOpen) {
                  removeToast(i);
                }
              }}
              open={!willRemove}
            />
          ))}
        </div>,
        bodyEl,
      )
    : null;
};
